import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { getAnalyticsDashboard } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  BarChart3,
  Eye,
  Search,
  MessageSquare,
  FileText,
  Download,
  TrendingUp,
  AlertTriangle,
  Activity,
  Loader2,
  ShieldAlert,
  Calendar,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  CartesianGrid,
} from 'recharts';

const COLORS = ['#2B8FD8', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

const MetricCard = ({ icon: Icon, label, value, subtitle, color = 'text-[#2B8FD8]' }) => (
  <Card className="border-slate-200">
    <CardContent className="p-5">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-slate-50`}>
          <Icon className={`h-5 w-5 ${color}`} />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-sm text-slate-500">{label}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
        <p className="text-sm font-medium text-slate-900">{label}</p>
        {payload.map((entry, index) => (
          <p key={entry.name || `tooltip-${index}`} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function AnalyticsPage() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAnalyticsDashboard(days);
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('admin_only');
      } else {
        setError(err.message || 'Failed to load analytics');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <ShieldAlert className="h-16 w-16 text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700 mb-2">Admin Access Required</h2>
          <p className="text-slate-500">Analytics dashboard is only available to administrators.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-[#2B8FD8] mb-4" />
          <p className="text-slate-500">Loading analytics...</p>
        </div>
      </Layout>
    );
  }

  if (error === 'admin_only') {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <ShieldAlert className="h-16 w-16 text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700 mb-2">Admin Access Required</h2>
          <p className="text-slate-500">Analytics dashboard is only available to administrators.</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <AlertTriangle className="h-16 w-16 text-amber-400 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700 mb-2">Failed to Load</h2>
          <p className="text-slate-500 mb-4">{error}</p>
          <Button onClick={fetchData}>Retry</Button>
        </div>
      </Layout>
    );
  }

  // Prepare chart data
  const topDocsData = (data?.top_viewed_docs || []).map((d) => ({
    name: (d.title || d._id || 'Unknown').substring(0, 24),
    views: d.views || 0,
  }));

  const eventBreakdown = (data?.event_breakdown || []).map((e) => ({
    name: (e._id || '').replace('_', ' '),
    value: e.count || 0,
  }));

  const dailyActivity = (data?.daily_activity || []).map((d) => ({
    date: d._id ? d._id.slice(5) : '', // MM-DD
    events: d.events || 0,
  }));

  const exportData = (data?.exports_by_format || []).map((e) => ({
    name: (e._id || 'unknown').toUpperCase(),
    count: e.count || 0,
  }));

  const genStats = data?.doc_generation_stats || {};
  const successRate = genStats.total
    ? Math.round(((genStats.successes || 0) / genStats.total) * 100)
    : 0;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-[#2B8FD8]" />
              Analytics Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Usage insights for the last {days} days
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-400" />
            {[7, 14, 30, 90].map((d) => (
              <Button
                key={d}
                size="sm"
                variant={days === d ? 'default' : 'outline'}
                className={days === d ? 'bg-[#2B8FD8] hover:bg-[#2477B8]' : ''}
                onClick={() => setDays(d)}
              >
                {d}d
              </Button>
            ))}
          </div>
        </div>

        {/* Metric Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <MetricCard icon={Activity} label="Total Events" value={(data?.total_events || 0).toLocaleString()} color="text-[#2B8FD8]" />
          <MetricCard
            icon={Eye}
            label="Doc Views"
            value={
              (data?.event_breakdown || []).find((e) => e._id === 'doc_viewed')?.count?.toLocaleString() || '0'
            }
            color="text-emerald-600"
          />
          <MetricCard
            icon={Search}
            label="Searches"
            value={
              (data?.event_breakdown || []).find((e) => e._id === 'search')?.count?.toLocaleString() || '0'
            }
            color="text-amber-600"
          />
          <MetricCard
            icon={MessageSquare}
            label="AI Queries"
            value={
              (data?.event_breakdown || []).find((e) => e._id === 'chat_query')?.count?.toLocaleString() || '0'
            }
            color="text-purple-600"
          />
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Daily Activity Trend */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-[#2B8FD8]" />
                Activity Trend
              </CardTitle>
              <CardDescription>Events per day</CardDescription>
            </CardHeader>
            <CardContent>
              {dailyActivity.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={dailyActivity}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="events" stroke="#2B8FD8" fill="#2B8FD8" fillOpacity={0.1} name="Events" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[240px] flex items-center justify-center text-slate-400 text-sm">
                  No activity data yet. Start browsing docs, searching, and using AI chat.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Event Breakdown Pie */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4 text-emerald-600" />
                Event Breakdown
              </CardTitle>
              <CardDescription>By event type</CardDescription>
            </CardHeader>
            <CardContent>
              {eventBreakdown.length > 0 ? (
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width="50%" height={220}>
                    <PieChart>
                      <Pie
                        data={eventBreakdown}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        innerRadius={40}
                      >
                        {eventBreakdown.map((entry, idx) => (
                          <Cell key={entry.name || `cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-2">
                    {eventBreakdown.map((item, idx) => (
                      <div key={item.name || `event-${idx}`} className="flex items-center gap-2 text-sm">
                        <div
                          className="w-3 h-3 rounded-full shrink-0"
                          style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                        />
                        <span className="text-slate-600 capitalize truncate">{item.name}</span>
                        <span className="text-slate-900 font-medium ml-auto">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-[220px] flex items-center justify-center text-slate-400 text-sm">
                  No events recorded yet.
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Top Viewed Docs */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Eye className="h-4 w-4 text-emerald-600" />
                Top Viewed Documents
              </CardTitle>
              <CardDescription>Most accessed documentation</CardDescription>
            </CardHeader>
            <CardContent>
              {topDocsData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={topDocsData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" width={120} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="views" fill="#10B981" radius={[0, 4, 4, 0]} name="Views" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[240px] flex items-center justify-center text-slate-400 text-sm">
                  No document views tracked yet.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Export Activity */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Download className="h-4 w-4 text-amber-600" />
                Export Activity
              </CardTitle>
              <CardDescription>Downloads by format</CardDescription>
            </CardHeader>
            <CardContent>
              {exportData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={exportData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Exports" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[240px] flex items-center justify-center text-slate-400 text-sm">
                  No exports recorded yet. Use Word or PDF export features.
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Doc Generation Stats */}
        <Card className="border-slate-200 mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4 text-purple-600" />
              Documentation Generation
            </CardTitle>
            <CardDescription>AI generation success rate and performance</CardDescription>
          </CardHeader>
          <CardContent>
            {genStats.total ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-slate-900">{genStats.total || 0}</p>
                  <p className="text-sm text-slate-500">Total Generations</p>
                </div>
                <div className="text-center">
                  <p className={`text-3xl font-bold ${successRate >= 80 ? 'text-emerald-600' : successRate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                    {successRate}%
                  </p>
                  <p className="text-sm text-slate-500">Success Rate</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-slate-900">
                    {genStats.avg_duration ? `${Math.round(genStats.avg_duration)}s` : 'N/A'}
                  </p>
                  <p className="text-sm text-slate-500">Avg Duration</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-slate-900">
                    {genStats.avg_files ? Math.round(genStats.avg_files) : 'N/A'}
                  </p>
                  <p className="text-sm text-slate-500">Avg Files Analyzed</p>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center text-slate-400 text-sm">
                No documentation generation events recorded yet. Upload or import code to generate docs.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tables Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Empty Searches (Doc Gaps) */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                Documentation Gaps
              </CardTitle>
              <CardDescription>Search queries with no results — consider adding docs for these topics</CardDescription>
            </CardHeader>
            <CardContent>
              {(data?.empty_searches || []).length > 0 ? (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {data.empty_searches.map((s, idx) => (
                    <div key={s._id || `empty-${idx}`} className="flex items-center justify-between py-2 px-3 rounded-md bg-red-50 border border-red-100">
                      <span className="text-sm text-slate-700 truncate flex-1 mr-2">"{s._id}"</span>
                      <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-0.5 rounded-full shrink-0">
                        {s.count} {s.count === 1 ? 'search' : 'searches'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-slate-400 text-sm">
                  No failed searches yet — your docs are covering everything!
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Searches */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Search className="h-4 w-4 text-[#2B8FD8]" />
                Top Search Queries
              </CardTitle>
              <CardDescription>Most frequently searched terms</CardDescription>
            </CardHeader>
            <CardContent>
              {(data?.top_searches || []).length > 0 ? (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {data.top_searches.map((s, idx) => (
                    <div key={s._id || `search-${idx}`} className="flex items-center justify-between py-2 px-3 rounded-md bg-slate-50 border border-slate-100">
                      <span className="text-sm text-slate-700 truncate flex-1 mr-2">"{s._id}"</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-slate-500">
                          avg {Math.round(s.avg_results || 0)} results
                        </span>
                        <span className="text-xs font-medium text-[#2B8FD8] bg-blue-50 px-2 py-0.5 rounded-full">
                          {s.count}x
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-slate-400 text-sm">
                  No searches recorded yet.
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Chat Usage by Project */}
        <Card className="border-slate-200 mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-purple-600" />
              AI Chat Usage by Project
            </CardTitle>
            <CardDescription>Which projects generate the most questions</CardDescription>
          </CardHeader>
          <CardContent>
            {(data?.chat_usage_by_project || []).length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-100">
                      <th className="py-2 px-3 font-medium">Project</th>
                      <th className="py-2 px-3 font-medium text-right">Queries</th>
                      <th className="py-2 px-3 font-medium text-right">Avg Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.chat_usage_by_project.map((c, idx) => (
                      <tr key={c._id || `chat-proj-${idx}`} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="py-2.5 px-3 text-slate-700">{c.project_name || c._id || 'Unknown'}</td>
                        <td className="py-2.5 px-3 text-right font-medium text-slate-900">{c.queries}</td>
                        <td className="py-2.5 px-3 text-right">
                          <span className={`font-medium ${(c.avg_confidence || 0) >= 0.7 ? 'text-emerald-600' : (c.avg_confidence || 0) >= 0.4 ? 'text-amber-600' : 'text-red-600'}`}>
                            {c.avg_confidence ? `${Math.round(c.avg_confidence * 100)}%` : 'N/A'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-8 text-center text-slate-400 text-sm">
                No AI chat queries recorded yet. Start asking questions in the project AI chat panel.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
